import type { HistRow, ScheduleDetailModel, ScheduleRow, StopWords } from "../app/mandates-model.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { frame } from "./sheet.js";

/**
 * DCTA-209 F58/F59 (09-scheduled.md §2.3, mock-up 23-scheduled.html `list` · `detail` · `stop-confirm` · `stopped`) — the
 * Scheduled list and a schedule's screen, two screens in one stack (as Payees and a payee are): a schedule pushes in from
 * the right over the list, or shows at once when it was opened from elsewhere (Activity's section, the chat). No tab bar on
 * either (the mock-up's pushed screens). Stop is pinned in the form bar under a schedule — one tap away at every scroll
 * position — and stays when the bank is unreachable: a protective action is never hidden.
 *
 * Lists are lists: `role="list"` named by their title, each row a `listitem` holding its button (a button never takes the
 * listitem role: it would stop being a button to a screen reader). Every string from the bank goes in as text.
 */
export interface ScheduledHandlers {
  onBack(): void;
  onOpen(mandateId: string): void;
  onDetailBack(): void;
  onRetryList(): void;
  onRetryDetail(): void;
  onStop(): void;
  onChange(): void;
}

let titles = 0;

/** A schedule as a row (28b): the tile, who, the rule with the date kept whole, the amount ("at most" under a cap), the chevron. */
export function scheduleRow(r: ScheduleRow, onOpen: (id: string) => void): HTMLElement {
  const button = el("button", { class: "raw item", type: "button", aria: { label: r.label }, data: { mandate: r.id } }, [
    el("span", { class: "ic", aria: { hidden: "true" } }, [icon(r.ic)]),
    el("span", { class: "tx" }, [el("b", {}, [r.who]), el("small", {}, [r.rule, el("span", { class: "nw" }, [r.date])])]),
    el("span", { class: `amt money${r.muted ? " muted" : ""}` }, [r.amount, ...(r.max ? [el("small", {}, ["at most"])] : [])]),
    icon("i-chev-r", "chev"),
  ]);
  button.addEventListener("click", () => onOpen(r.id));
  return el("div", { role: "listitem" }, [button]);
}

function histRow(h: HistRow): HTMLElement {
  return el("div", { class: "item", role: "listitem" }, [
    el("span", { class: `ic${h.tone === "" ? "" : ` ${h.tone}`}`, aria: { hidden: "true" } }, [icon(h.ic)]),
    el("span", { class: "tx" }, [el("b", {}, [h.title]), el("small", {}, [el("span", { class: "st" }, [h.st]), h.rest])]),
    ...(h.amount === "" ? [] : [el("span", { class: `amt money${h.muted ? " muted" : ""}` }, [h.amount])]),
  ]);
}

/** The bank unreachable (Activity's banner, accepted as built, row 28e): what was on screen stays; Retry reads again. */
function unreachableBanner(cls: string, onRetry: () => void): HTMLElement {
  const retry = el("button", { class: "raw", type: "button" }, ["Retry"]);
  retry.addEventListener("click", onRetry);
  return el("div", { class: `banner ${cls}`, role: "status", hidden: true }, [icon("i-alert"), el("span", { class: "bt" }, [el("span", {}, ["Can't reach the bank right now. Nothing has changed."])]), retry]);
}

export class ScheduledView {
  readonly parts: readonly HTMLElement[];
  readonly listBody: HTMLElement;
  readonly detailBody: HTMLElement;
  readonly #h: ScheduledHandlers;
  readonly #list: HTMLElement;
  readonly #listNote: HTMLElement;
  readonly #listBanner: HTMLElement;
  readonly #rows: HTMLElement;
  readonly #endedWrap: HTMLElement;
  readonly #endedRows: HTMLElement;
  readonly #empty: HTMLElement;
  readonly #skel: HTMLElement;
  readonly #detail: HTMLElement;
  readonly #detailBack: HTMLButtonElement;
  readonly #detailNote: HTMLElement;
  readonly #detailBanner: HTMLElement;
  readonly #stopped: HTMLElement;
  readonly #hero: HTMLElement;
  readonly #kv: HTMLElement;
  readonly #hist: HTMLElement;
  readonly #content: HTMLElement;
  readonly #detailSkel: HTMLElement;
  readonly #formbar: HTMLElement;
  readonly #stopBtn: HTMLButtonElement;
  readonly #stopLbl: HTMLElement;
  readonly #change: HTMLButtonElement;
  /** The row that opened the schedule: focus goes back to it on Back. */
  #opener: string | undefined;

  constructor(handlers: ScheduledHandlers) {
    this.#h = handlers;
    titles += 1;
    const listTitle = `sch-title-${String(titles)}`;
    const endedTitle = `sch-ended-${String(titles)}`;
    const histTitle = `sch-hist-${String(titles)}`;
    const back = el("button", { class: "raw icobtn back", type: "button", aria: { label: "Back to Activity" } }, [icon("i-chev-l")]);
    back.addEventListener("click", () => this.#h.onBack());
    this.#listNote = el("span", { class: "vis-hidden loading-note", role: "status" });
    this.#listBanner = unreachableBanner("lserr", () => this.#h.onRetryList());
    this.#rows = el("div", { class: "list feed sch all-rows", role: "list", aria: { labelledby: listTitle } });
    this.#endedRows = el("div", { class: "list feed sch ended-rows", role: "list", aria: { labelledby: endedTitle } });
    this.#endedWrap = el("div", { class: "ended-wrap", hidden: true }, [el("div", { class: "sec" }, [el("h3", { id: endedTitle }, ["Ended"])]), this.#endedRows]);
    this.#empty = el("div", { class: "empty", hidden: true }, [
      el("span", { class: "ic", aria: { hidden: "true" } }, [icon("i-calendar")]),
      el("b", {}, ["Nothing scheduled"]),
      el("p", {}, ["Payments you set up for later, or to repeat, show up here."]),
    ]);
    this.#skel = el("div", { class: "list sch-skel", aria: { hidden: "true" }, hidden: true }, [el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" })]);
    this.listBody = el("div", { class: "body" }, [this.#listNote, this.#listBanner, this.#skel, this.#rows, this.#endedWrap, this.#empty, el("div", { class: "home-end", aria: { hidden: "true" } })]);
    this.#list = el("div", { class: "scr s-list", role: "region", aria: { labelledby: listTitle } }, [
      el("div", { class: "topbar" }, [back, el("span", { class: "t", id: listTitle }, ["Scheduled"]), el("span", { class: "topbar-pad", aria: { hidden: "true" } })]),
      this.listBody,
      el("span", { class: "veil", aria: { hidden: "true" } }),
    ]);

    this.#detailBack = el("button", { class: "raw icobtn back", type: "button", aria: { label: "Back to Scheduled" } }, [icon("i-chev-l")]);
    this.#detailBack.addEventListener("click", () => this.#h.onDetailBack());
    this.#detailNote = el("span", { class: "vis-hidden dt-note", role: "status" });
    this.#detailBanner = unreachableBanner("dterr", () => this.#h.onRetryDetail());
    this.#stopped = el("div", { class: "banner done stopban", role: "status", tabIndex: -1, hidden: true });
    this.#hero = el("div", { class: "dhero" });
    this.#kv = el("div", { class: "list kv stack dkv" });
    this.#hist = el("div", { class: "list feed hist", role: "list", aria: { labelledby: histTitle } });
    this.#content = el("div", { class: "dcontent" }, [
      this.#hero,
      el("div", { class: "sec" }, [el("h3", {}, ["Details"])]),
      this.#kv,
      el("div", { class: "sec" }, [el("h3", { id: histTitle }, ["Payments"])]),
      this.#hist,
    ]);
    this.#detailSkel = el("div", { class: "sch-dskel", aria: { hidden: "true" }, hidden: true }, [
      el("span", { class: "sk sk-hero" }),
      el("span", { class: "sk sk-dayline" }),
      el("div", { class: "list" }, [el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" })]),
    ]);
    this.detailBody = el("div", { class: "body" }, [this.#detailNote, this.#detailBanner, this.#stopped, this.#detailSkel, this.#content, el("div", { class: "home-end", aria: { hidden: "true" } })]);
    this.#stopLbl = el("span", { class: "lbl" }, ["Stop payments"]);
    this.#stopBtn = el("button", { class: "raw btn stopgo", type: "button" }, [icon("i-stop"), this.#stopLbl]);
    this.#stopBtn.addEventListener("click", () => this.#h.onStop());
    this.#change = el("button", { class: "raw btn link change", type: "button" }, ["Change amount or day"]);
    this.#change.addEventListener("click", () => this.#h.onChange());
    this.#formbar = el("div", { class: "formbar", hidden: true }, [this.#stopBtn, this.#change]);
    this.#detail = el("div", { class: "scr s-detail off-r", role: "region", aria: { label: "Scheduled payment", hidden: "true" } }, [
      el("div", { class: "topbar" }, [this.#detailBack, el("span", { class: "t" }, ["Scheduled payment"]), el("span", { class: "topbar-pad", aria: { hidden: "true" } })]),
      this.detailBody,
      this.#formbar,
    ]);
    this.#detail.inert = true;
    this.parts = [el("div", { class: "screens" }, [this.#list, this.#detail])];
  }

  get detailOpen(): boolean {
    return !this.#detail.classList.contains("off-r");
  }

  /** The Stop button under a schedule (focus returns here when the stop sheet closes without stopping). */
  get stopButton(): HTMLButtonElement {
    return this.#stopBtn;
  }

  // ----------------------------------------------------------------- the list
  loadingList(): void {
    this.listBody.setAttribute("aria-busy", "true");
    this.#listNote.textContent = "Loading your scheduled payments";
    this.#skel.hidden = false;
    this.#rows.replaceChildren();
    this.#endedWrap.hidden = true;
    this.#empty.hidden = true;
  }

  showList(m: { readonly active: readonly ScheduleRow[]; readonly ended: readonly ScheduleRow[] }): void {
    this.#idleList();
    this.#listBanner.hidden = true;
    // A read again (Back from a schedule) redraws the rows: focus stays on the same schedule's row, wherever it now sits.
    const active = document.activeElement;
    const focused = active instanceof HTMLElement && this.#list.contains(active) ? active.dataset["mandate"] : undefined;
    this.#rows.replaceChildren(...m.active.map((r) => scheduleRow(r, (id) => this.#open(id))));
    this.#endedRows.replaceChildren(...m.ended.map((r) => scheduleRow(r, (id) => this.#open(id))));
    this.#rows.hidden = m.active.length === 0;
    this.#endedWrap.hidden = m.ended.length === 0;
    this.#empty.hidden = m.active.length + m.ended.length > 0;
    if (focused !== undefined) this.#list.querySelector<HTMLElement>(`button.item[data-mandate="${CSS.escape(focused)}"]`)?.focus({ preventScroll: true });
  }

  /** The read failed: what was on screen stays (or the empty frame), the banner says so and offers Retry. */
  listUnreachable(): void {
    this.#idleList();
    this.#listBanner.hidden = false;
  }

  #idleList(): void {
    this.listBody.setAttribute("aria-busy", "false");
    this.#listNote.textContent = "";
    this.#skel.hidden = true;
  }

  #open(id: string): void {
    this.#opener = id;
    this.#h.onOpen(id);
  }

  // ------------------------------------------------------------- a schedule
  /** A schedule's screen, pushed in from the right over the list — or shown at once (`instant`) over Activity or the chat. */
  pushDetail(backLabel: string, instant: boolean): void {
    this.#detailBack.setAttribute("aria-label", backLabel);
    if (instant) this.#detail.dataset["instant"] = "true";
    this.#detail.classList.remove("off-r");
    this.#list.classList.add("under");
    this.#list.inert = true;
    this.#list.setAttribute("aria-hidden", "true");
    this.#detail.inert = false;
    this.#detail.removeAttribute("aria-hidden");
    this.detailBody.scrollTop = 0;
    this.#detailBack.focus({ preventScroll: true });
    if (instant) requestAnimationFrame(() => delete this.#detail.dataset["instant"]);
  }

  popDetail(): void {
    if (!this.detailOpen) return;
    this.#detail.classList.add("off-r");
    this.#detail.inert = true;
    this.#detail.setAttribute("aria-hidden", "true");
    this.#list.classList.remove("under");
    this.#list.inert = false;
    this.#list.removeAttribute("aria-hidden");
    this.#stopped.hidden = true;
    const opener = this.#opener === undefined ? null : this.#list.querySelector<HTMLElement>(`button.item[data-mandate="${CSS.escape(this.#opener)}"]`);
    opener?.focus({ preventScroll: true });
    this.#opener = undefined;
  }

  loadingDetail(): void {
    this.detailBody.setAttribute("aria-busy", "true");
    this.#detailNote.textContent = "Loading this scheduled payment";
    this.#detailSkel.hidden = false;
    this.#content.hidden = true;
    this.#detailBanner.hidden = true;
    this.#stopped.hidden = true;
    this.#formbar.hidden = true;
  }

  showDetail(d: ScheduleDetailModel): void {
    this.detailBody.setAttribute("aria-busy", "false");
    this.#detailNote.textContent = "";
    this.#detailSkel.hidden = true;
    this.#content.hidden = false;
    this.#detailBanner.hidden = true;
    const h = d.hero;
    this.#hero.replaceChildren(
      el("div", { class: "nm" }, [el("span", { class: "dwho" }, [h.who]), " ", el("span", { class: "d" }, [h.sub])]),
      el("div", { class: "big money" }, [h.amount, ...(h.max ? [el("small", {}, ["at most"])] : [])]),
      el("div", { class: "due" }, [h.rule]),
      el("div", { class: "pl" }, [el("span", { class: "plk" }, [h.status]), el("span", { class: "flag" }, [h.flag])]),
    );
    this.#kv.replaceChildren(
      ...d.kv.map((r) => el("div", {}, [el("span", { class: "k" }, [r.k]), el("span", { class: "v" }, [r.v, ...(r.small === undefined ? [] : [el("small", {}, [r.small])])])])),
    );
    this.#hist.replaceChildren(...d.history.map(histRow));
    const a = d.actions;
    this.#formbar.hidden = a === undefined;
    if (a !== undefined) {
      this.#stopLbl.textContent = a.stop;
      this.#change.textContent = a.change;
    }
  }

  /** The read failed: the banner over what was last read; Stop stays where it was (never hidden because the bank blinked). */
  detailUnreachable(): void {
    this.detailBody.setAttribute("aria-busy", "false");
    this.#detailNote.textContent = "";
    this.#detailSkel.hidden = true;
    this.#content.hidden = false;
    this.#detailBanner.hidden = false;
  }

  /**
   * A stop that found the schedule already ended another way (it ran, was replaced, ran out): no Stopped line — the screen
   * shows the bank's state (its flag, no Stop) and focus goes to it, so a screen reader hears what is true.
   */
  settled(): void {
    this.#stopped.hidden = true;
    this.detailBody.scrollTop = 0;
    this.#hero.tabIndex = -1;
    this.#hero.focus({ preventScroll: true });
  }

  /** 28k: the Stopped line takes the top and focus (`role="status"`, never a toast). */
  stopped(words: { readonly title: string; readonly rest: string }): void {
    this.#stopped.replaceChildren(icon("i-check"), el("span", { class: "bt" }, [el("b", {}, [words.title]), el("span", {}, [words.rest])]));
    this.#stopped.hidden = false;
    this.#formbar.hidden = true;
    this.detailBody.scrollTop = 0;
    this.#stopped.focus({ preventScroll: true });
  }
}

/**
 * 28j/28l: the stop sheet, in the shape of 19's lock sheet — the stop mark, the question, the payment, "already sent stay
 * sent", Stop payments and Keep them. No passkey, no token, no countdown. While the bank is asked the act is busy (label
 * kept) and a second tap asks nothing; a stop that did not get through puts the loud line in the sheet (`role="alert"`,
 * protection did not go on) and the act becomes Try again.
 */
export function stopSheet(
  w: StopWords,
  h: { readonly onAct: () => void; readonly onKeep: () => void },
): { readonly sheet: HTMLElement; readonly primary: HTMLButtonElement; busy(on: boolean): void; failed(on: boolean): void } {
  // aria-busy is for screen readers while the bank is asked; the one-request guard is the screen's (ScheduledScreen #stop).
  const primary = el("button", { class: "raw btn dostop", type: "button", aria: { busy: "false" } }, [w.act]);
  primary.addEventListener("click", h.onAct);
  const keep = el("button", { class: "raw btn link keep", type: "button" }, [w.keep]);
  keep.addEventListener("click", h.onKeep);
  const loud = el("div", { class: "banner loud sterr", role: "alert", hidden: true }, [icon("i-alert"), el("span", { class: "bt" }, [el("b", {}, [w.failed.lead]), el("span", {}, [w.failed.rest])])]);
  const sheet = frame(w.title, el("div", { class: "key", aria: { hidden: "true" } }, [icon("i-stop")]), [
    el("p", { class: "lead" }, [...(w.pre === undefined ? [] : [w.pre]), el("span", { class: "money" }, [w.amount]), w.lead]),
    w.fine === undefined ? null : el("p", { class: "fine" }, [w.fine]),
    loud,
    el("div", { class: "acts" }, [primary, keep]),
  ]);
  sheet.classList.add("short", "s-stop");
  return {
    sheet,
    primary,
    // While the bank is asked: the act busy (its label kept), Keep them not available — the answer lands in this sheet.
    busy(on: boolean): void {
      primary.setAttribute("aria-busy", on ? "true" : "false");
      keep.setAttribute("aria-disabled", on ? "true" : "false");
    },
    failed(on: boolean): void {
      loud.hidden = !on;
      primary.textContent = on ? w.failed.act : w.act;
    },
  };
}
